<script setup lang="ts">
import { onMounted, ref, toRefs, watch, computed } from 'vue';
import Chip from 'primevue/chip';
import Dialog from 'primevue/dialog';
import Fieldset from 'primevue/fieldset';
import { FilterMatchMode } from 'primevue/api';
import { useDateTime } from '@/composables';
import { EMIT } from '@/enum';
import { IntegrationSchoolDayService } from '@/services';
import { useStudentReferralStore } from '@/store';
import { OrganizationInterface } from '@/types';

interface PropsInterface {
  open: boolean;
  organization: OrganizationInterface;
}
const emit = defineEmits([EMIT.CANCEL, EMIT.UPDATE]);

const props = defineProps<PropsInterface>();

const { open, organization } = toRefs(props);

const referralType = ref(null);

const referralsSelected = ref<number[]>([]);

const referralStore = useStudentReferralStore();

const students = ref([]);

watch(organization, () => init());

onMounted(async () => init());

const canImport = computed(() => !(Boolean(referralType.value) && Boolean(referralsSelected.value.length)));

const hasFiltersSet = computed(() => {
  return Object.keys(filters.value).some((key) => {
    const filter = filters.value[key];
    return filter.value !== null && filter.value !== '' && filter.value !== undefined;
  });
});

const init = async () => {
  const schoolId = organization.value.integration_organization.sis_id;
  const response = await IntegrationSchoolDayService.organizationStudents(null, schoolId, 'student_last_name', 'desc');
  students.value = response.data.data;
};

const onCancel = () => {
  referralsSelected.value = [];
  referralType.value = null;
  filters.value.global.value = null;
  filters.value['student_grade.grade'].value = null;
  emit(EMIT.CANCEL);
};

const onImport = () => {
  referralStore.onSISImport(referralType.value, referralsSelected.value);
  onCancel();
  emit(EMIT.UPDATE);
};

const onFilterClear = () => {
  filters.value.global.value = null;
  filters.value['student_grade.grade'].value = null;
};

const filters = ref({
  global: { value: null, matchMode: FilterMatchMode.CONTAINS },
  'student_grade.grade': { value: null, matchMode: FilterMatchMode.EQUALS },
});

const loading = ref(false);

const onSort = async (event) => {
  loading.value = true;
  const orderBy = event.sortField;
  const order = event.sortOrder === 1 ? 'asc' : 'desc';
  const schoolId = organization.value.integration_organization.sis_id;
  const response = await IntegrationSchoolDayService.organizationStudents(null, schoolId, orderBy, order);
  students.value = response.data.data;
  loading.value = false;
};
</script>

<template>
  <Dialog
    v-model:visible="open"
    modal
    :style="{ width: '75vw' }"
    class="dialog__container"
    :closable="false"
    :close-on-escape="false"
    :block-scroll="true"
    :draggable="false"
    @hide="onCancel"
  >
    <template #header>
      <div class="flex flex-column w-full">
        <h2 class="flex mb-3 px-3">
          <span class="mr-1">SIS Student Import /</span>
          <span class="text-primary" v-text="organization.name" />
        </h2>
        <div class="flex flex-wrap gap-3 px-2">
          <div class="flex align-items-center gap-3 flex-1">
            <div class="field p-3 w-full bg-gray-50 border-1 border-primary-300 border-round-md">
              <label for="referral_type" class="required">Referral Type</label>
              <Dropdown
                v-model="referralType"
                type="secondary"
                :options="referralStore.referralTypes"
                option-label="title"
                option-value="id"
                q
                placeholder="Select Referral Type"
                class="w-full"
              />
            </div>
            <div class="field mt-5 w-full">
              <IconField icon-position="left">
                <InputIcon>
                  <Icon name="search" class="text-lg" />
                </InputIcon>
                <InputText v-model="filters.global.value" class="w-full" placeholder="Search by Student Name" />
              </IconField>
            </div>
            <div class="field mt-5 w-full">
              <Dropdown
                v-model="filters['student_grade.grade'].value"
                type="secondary"
                :options="referralStore.gradeOptions"
                show-clear
                placeholder="Filter By Grade"
                class="w-full"
              />
            </div>
          </div>
          <div class="flex mt-6">
            <AppButton v-if="hasFiltersSet" class="mr-3" type="secondary" text="Clear Filters" @click="onFilterClear" />
            <AppButton class="mr-3" type="secondary" text="Cancel" @click="onCancel" />
            <AppButton type="primary" text="Import" :disabled="canImport" @click="onImport" />
          </div>
        </div>
      </div>
    </template>
    <template #default>
      <div class="flex">
        <div class="flex flex-grow-1 p-2 pt-0 w-full" style="height: 100vh">
          <DataTable
            v-model:selection="referralsSelected"
            v-model:filters="filters"
            :value="students"
            :loading="loading"
            loading-icon="logo-spinner"
            class="table-contents border-1 border-gray-100 border-round-md w-full"
            data-key="student_id"
            scrollable
            scroll-height="flex"
            @sort="onSort"
          >
            <Column selection-mode="multiple" header-style="max-width: 50px;" body-style="max-width: 50px;" />
            <Column header="SSID" field="student_id" sortable />
            <Column header="Student First Name" field="student_first_name" sortable />
            <Column header="Student Last Name" field="student_last_name" sortable />
            <Column header="Gender" field="student_gender" sortable />
            <Column header="Birth Date" field="student_birthdate" sortable>
              <template #body="{ data }">
                <span v-text="useDateTime().formatIntlDate(data.student_birthdate)" />
              </template>
            </Column>
            <Column header="Grade" filter-field="student_grade.grade" sort-field="student_grade" sortable>
              <template #body="{ data }">
                <span v-text="data.student_grade.grade" />
              </template>
            </Column>
            <Column header="Parent First Name" field="parent_first_name" sortable />
            <Column header="Parent Last Name" field="parent_last_name" sortable />
            <template #empty>There are no students available</template>
          </DataTable>
        </div>
        <div v-if="referralsSelected.length" class="w-3 xl:w-4 ml-2 mr-2">
          <Fieldset>
            <template #legend>
              <div class="flex align-items-center">
                <span class="font-bold">Students Selected</span>
                <Tag
                  :value="referralsSelected.length"
                  rounded
                  class="ml-2 border-circle w-1rem h-1rem text-xs text-white"
                />
              </div>
            </template>
            <Chip
              v-for="(item, key) in referralsSelected"
              :key="key"
              class="m-1"
              :label="`${item.student_first_name} ${item.student_last_name}`"
            />
          </Fieldset>
        </div>
      </div>
    </template>
  </Dialog>
</template>

<style lang="scss" scoped>
.dialog__container {
  overflow: hidden !important;

  .p-dialog-header {
    padding-left: 5px !important;
    padding-right: 5px !important;
    padding-bottom: 0 !important;
  }

  .p-dialog-content {
    padding-left: 8px !important;
    padding-right: 8px !important;
    padding-bottom: 5px !important;
    overflow: hidden !important;
  }

  .p-dialog-header,
  .p-dialog-content {
    background: var(--surface-0) !important;
  }

  .p-fieldset-legend {
    padding: 8px;
  }

  .p-fieldset-content {
    padding: 0;
    overflow: scroll !important;
    max-height: calc(100vh - 300px);
  }

  .table-contents {
    max-height: calc(100vh - 300px);
  }
}

.p-tag {
  padding: 12px !important;
}
</style>
